import React from 'react';
import { Button, Spinner, Text } from '@spark';

/**
 * Primary Next/Train action used by the sticky heading and by the DOM-last
 * overlay on `nextInHeading={false}` steps. Same size in idle and loading so
 * the overlay cannot grow over Back.
 */
export const WizardNextButton = ({
    onClick,
    disabled = false,
    loading = false,
    label = 'Next',
    loadingLabel = 'Loading…',
    disableWhenReadOnly = false,
}) => (
    <Button
        variant="primary"
        size="medium"
        disabled={disabled || loading}
        disableWhenReadOnly={disableWhenReadOnly}
        startIcon={loading ? <Spinner size="small" /> : undefined}
        onClick={onClick}
    >
        {loading ? loadingLabel : label}
    </Button>
);

/**
 * Shared chrome for wizard steps 2-5 and the task Model Summary tab (Figma
 * 3001-68512): task name above the step title on the left, optional Back
 * (secondary) + primary action on the right. `taskName` may be briefly empty
 * on a deep-linked resume until the usecase loads — the step title then
 * renders alone.
 *
 * `nextInHeading={false}`: the step renders the real Next button itself as
 * its LAST DOM child (so Tab reaches it after the whole step) and overlays
 * it onto this heading via the `.cw-step--next-last` grid; the heading then
 * renders only an invisible same-sized placeholder to reserve the slot so
 * Back keeps its position.
 */
const WizardStepHeading = ({
    title,
    taskName = null,
    onBack,
    onNext,
    nextLabel = 'Next',
    nextDisabled = false,
    nextLoading = false,
    nextLoadingLabel = 'Loading…',
    nextDisableWhenReadOnly = false,
    nextInHeading = true,
    errorMessage = null,
    className = 'cw-step-heading',
}) => (
    <>
        <div className={`${className} fs-unmask`}>
            <div className={`${className}__titles`}>
                {taskName && (
                    <Text variant="heading-3-semibold" color="primary" as="h2">
                        {taskName}
                    </Text>
                )}
                <Text variant="heading-4-semibold" color="primary" as="h3">
                    {title}
                </Text>
            </div>
            <div className={`${className}__actions`}>
                {onBack && (
                    <Button variant="secondary" size="medium" onClick={onBack}>
                        Back
                    </Button>
                )}
                {onNext && nextInHeading && (
                    <WizardNextButton
                        onClick={onNext}
                        disabled={nextDisabled}
                        loading={nextLoading}
                        label={nextLabel}
                        loadingLabel={nextLoadingLabel}
                        disableWhenReadOnly={nextDisableWhenReadOnly}
                    />
                )}
                {onNext && !nextInHeading && (
                    <span className="cw-step-heading__next-placeholder" aria-hidden="true">
                        <WizardNextButton
                            disabled
                            loading={nextLoading}
                            label={nextLabel}
                            loadingLabel={nextLoadingLabel}
                            disableWhenReadOnly={nextDisableWhenReadOnly}
                        />
                    </span>
                )}
            </div>
        </div>
        {errorMessage && (
            <div className={`${className}__error fs-unmask`}>{errorMessage}</div>
        )}
    </>
);

export default WizardStepHeading;
